import { useCallback, useState } from 'react'
import { Stack, Box, SimpleGrid, Heading, Text, Input, SearchableSelect, Checkbox, Alert, SimpleTable, RadioGroup, RadioHeader, TimeChooser, Button, Select, FormControl } from '@/components/lib'
import useStore from '@/store'
import utils from '@/utils/utils'

// Keep in step with the Range(min=1, max=...) validation on availabilityWindowHours
// in api/routes/stages.py — sending an out-of-range value fails the whole stage save.
const MAX_AVAILABILITY_WINDOW_HOURS = 8760 // one year

export default function Settings ({ stage, availableToSites, setAvailableToSites, enabled, setEnabled, enableAfter, setEnableAfter, enableAtTime, setEnableAtTime, availabilityWindowHours, setAvailabilityWindowHours, isRecurring, recurringFrequency, setRecurringFrequency, recurringDuration, setRecurringDuration, recurrenceNames, setRecurrenceNames, name, adHoc, setAdHoc, trial, sites, stages, isPoll, setIsRecurring, isAnchored, setIsAnchored, anchorSchedule, setAnchorSchedule, exemptFromBehindStatus, setExemptFromBehindStatus, behindCutoffTime, setBehindCutoffTime }) {
  const { anchors } = useStore()
  // null means "follow the stage": the list starts open if it already has names.
  // The stage loads after the first render, so this can't be a useState initialiser.
  const [showRecurrenceNames, setShowRecurrenceNames] = useState(null)
  const recurrenceNamesVisible = showRecurrenceNames ?? (recurrenceNames || []).some(n => n)

  // How many occurrences the name list should offer a row for.
  const occurrenceCount = parseInt(recurringDuration) || 0

  // What a participant sees for this occurrence when no name is set for it.
  function defaultRecurrenceName (index) {
    const template = name || stage?.name || ''
    return template.replaceAll('%n', String(index + 1)) || 'Stage name'
  }

  function updateRecurrenceName (index, value) {
    // Names are positional, so the array has to be padded out to the index
    // being edited rather than compacted down to the ones that are set.
    const length = Math.max(index + 1, (recurrenceNames || []).length)
    const updated = Array.from({ length }, (_, i) => (recurrenceNames || [])[i] || '')
    updated[index] = value
    setRecurrenceNames(updated)
  }

  function selectSchedule (val) {
    if (val === 'none') {
      setIsRecurring(false)
      setAdHoc(false)
      setIsAnchored(false)
      setRecurringFrequency(0)
      setRecurringDuration(0)
      setRecurrenceNames([])
    }
    if (val === 'recur') {
      setIsRecurring(true)
      setAdHoc(false)
      setIsAnchored(false)
    }
    if (val === 'adHoc') {
      setIsRecurring(false)
      setAdHoc(true)
      setIsAnchored(false)
      setRecurringFrequency(0)
      setRecurringDuration(0)
      setRecurrenceNames([])
    }
    if (val === 'anchored') {
      setIsRecurring(false)
      setAdHoc(false)
      setIsAnchored(true)
      setRecurringFrequency(0)
      setRecurringDuration(0)
      setRecurrenceNames([])
    }
  }

  function addAnchorSlot () {
    setAnchorSchedule([...(anchorSchedule || []), { anchor: anchors?.[0]?._id || '', dayOffset: 0 }])
  }

  function updateAnchorSlot (index, field, value) {
    const updated = (anchorSchedule || []).map((slot, i) =>
      i === index ? { ...slot, [field]: field === 'dayOffset' ? parseInt(value) || 0 : value } : slot
    )
    setAnchorSchedule(updated)
  }

  function removeAnchorSlot (index) {
    setAnchorSchedule((anchorSchedule || []).filter((_, i) => i !== index))
  }

  const updateEnableAfter = useCallback((data) => {
    // If switching type, reset irrelevant fields.
    if (data.type === 'anchor') {
      data.stage = null
      data.stageRecurrence = null
    }
    if (data.type === 'stage') {
      data.anchor = null
    }
    if (data.delay) {
      data.delay = parseInt(data.delay)
    }
    if (data.stage && (data.type || enableAfter?.type || 'stage') === 'stage') {
      if (data.stage.indexOf(':') > -1) {
        const components = data.stage.split(':')
        data.stage = components[0]
        data.stageRecurrence = parseInt(components[1])
      } else {
        data.stageRecurrence = null
      }
    }
    const newEnableAfter = Object.assign({}, enableAfter, data)
    setEnableAfter(newEnableAfter)
  }, [enableAfter])

  const enableAfterType = enableAfter?.type || 'stage'

  return (
    <Box>
      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Heading as='h3' size='md'>Stage scope</Heading>
        <Text color='fg.muted' mb={5}>Specify the sites with access to this stage. For example, you might use different form variants for different sites.</Text>

        <RadioGroup value={availableToSites ? 'none' : 'all'}>
          <SimpleGrid columns={2} gap={5}>
            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={!availableToSites} name='availableToSites' value='none' title='Do not restrict to specific sites'
                onChange={e => setAvailableToSites(null)}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>This stage will be made available to all sites, subject to the settings below.</Text>
                  </>
                }
              />
            </Box>
            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={availableToSites} name='availableToSites' value='none' title='Restrict to specific sites only'
                onChange={e => { if (!availableToSites) setAvailableToSites([]) }}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>This stage will only be available to selected sites.</Text>
                    {availableToSites &&
                      <SimpleTable
                        tableProps={{ variant: 'simple', size: 'sm' }}
                        rows={sites.map(site => [
                          <Text key={site._id}>
                            <Checkbox
                              isChecked={availableToSites.indexOf(site._id) > -1}
                              onChange={e => {
                                const index = availableToSites.indexOf(site._id)
                                const newSites = Object.assign([], availableToSites)
                                if (index === -1) newSites.push(site._id)
                                else newSites.splice(index, 1)
                                setAvailableToSites(newSites)
                              }}
                            />
                          </Text>,
                          site.name
                        ])}
                      />}
                  </>
                }
              />
            </Box>
          </SimpleGrid>
        </RadioGroup>
      </Box>

      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Heading as='h3' size='md'>Stage availability</Heading>
        <Text color='fg.muted' mb={5}>Choose whether this stage is available to participants to complete themselves (participant-reported outcomes) or not.</Text>

        {isPoll &&
          <Alert status='info' variant='left-accent' mb={5}>
            Since poll mode is enabled for this stage, viewers with the link to the poll will be able to view and submit responses to the poll at any time. The settings below only apply to full trial participants using the participant portal or mobile apps.
          </Alert>}

        <RadioGroup>
          <SimpleGrid columns={3} gap={3}>
            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={!enabled && !enableAfter} name='enable' value='none' title='Not available to participants'
                onChange={e => { setEnabled(false); setEnableAfter(null) }}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>This stage is not available to participants to complete. Select this option if staff will complete this data on behalf of the participants.</Text>
                  </>
                }
              />
            </Box>

            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={enabled && !enableAfter} name='enable' value='true' title='Available to participants'
                onChange={e => { setEnabled(true); setEnableAfter(null) }}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>The stage is immediately available for completion by participants themselves using the mobile app or participant portal.</Text>
                  </>
                }
              />
            </Box>

            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={!!enableAfter} name='enable' value='after' title='Make available after...'
                onChange={e => { setEnabled(false); setEnableAfter({ type: 'stage', stage: null, stageRecurrence: null, anchor: null, delay: 0 }) }}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>Enable this stage for participants to complete after a trigger event. This stage might become available to each participant on different dates, depending on when they meet the trigger.</Text>
                    {!!enableAfter &&
                      <Stack direction='column' onClick={e => e.stopPropagation()}>
                        <Stack direction='row' alignItems='center' justifyContent='center'>
                          <Text fontSize='sm'>Trigger</Text>
                          <Select
                            size='xs'
                            bg='bg.surface'
                            w='220px'
                            value={enableAfterType}
                            onChange={e => updateEnableAfter({ type: e.target.value })}
                          >
                            <option value='stage'>Stage completion</option>
                            <option value='anchor'>Anchor date</option>
                          </Select>
                        </Stack>
                        <Stack direction='row' alignItems='center' justifyContent='center'>
                          <Text fontSize='sm'>Enable</Text>
                          <Input size='xs' placeholder='2' bg='bg.surface' w='50px' value={enableAfter?.delay ?? ''} onChange={e => updateEnableAfter({ delay: e.target.value })} />
                          <Text fontSize='sm'>days after</Text>
                        </Stack>
                        {enableAfterType === 'stage'
                          ? (
                            <>
                              <Text fontSize='sm'>the following stage is completed:</Text>
                              <SearchableSelect
                                size='sm' bg='bg.surface' placeholder='Select another stage' w='100%'
                                value={`${enableAfter?.stage || ''}${enableAfter?.stageRecurrence != null ? `:${enableAfter?.stageRecurrence}` : ''}`}
                                onChange={selected => updateEnableAfter({ stage: selected })}
                                options={(() => {
                                  const options = []
                                  stages?.filter(s => s._id !== stage?._id).forEach(s => {
                                    if (s.isRecurring) {
                                      Array.from({ length: s.recurringDuration }, (_, i) => i).forEach(recurrence =>
                                        options.push({ value: `${s._id}:${recurrence}`, label: `${s.name} (${utils.recurrenceCustomName(s, recurrence) || `recurr. ${recurrence + 1}`})` })
                                      )

                                      // If the editing stage is recurring, offer to match with the recurrence of the option stage
                                      // indicate match with a special value of -1
                                      if (isRecurring) {
                                        options.push({ value: `${s._id}:-1`, label: `${s.name} (match recurrence)` })
                                      }
                                    } else {
                                      options.push({ value: s._id, label: s.name })
                                    }
                                  })
                                  return options
                                })()}
                              />
                            </>
                            )
                          : (
                            <>
                              {anchors?.length === 0
                                ? <Alert status='warning' fontSize='sm'>No anchors defined. Add anchors on the Anchors tab first.</Alert>
                                : (
                                  <>
                                    <Text fontSize='sm'>the following anchor date:</Text>
                                    <SearchableSelect
                                      size='sm'
                                      bg='bg.surface'
                                      placeholder='Select an anchor'
                                      w='100%'
                                      value={enableAfter?.anchor || ''}
                                      onChange={selected => updateEnableAfter({ anchor: selected })}
                                      options={(anchors || []).map(a => ({ value: a._id, label: a.name }))}
                                    />
                                  </>
                                  )}
                            </>
                            )}
                      </Stack>}
                  </>
                }
              />
            </Box>
          </SimpleGrid>
        </RadioGroup>
      </Box>

      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Heading as='h3' size='md'>Schedule stage</Heading>
        <Text color='fg.muted' mb={5}>Choose whether this stage can be completed multiple times or not.</Text>

        <RadioGroup>
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={!isRecurring && !adHoc && !isAnchored} name='schedule' value='none' title='This stage will not repeat'
                onChange={e => selectSchedule('none')}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>The stage can only be completed once.</Text>
                  </>
              }
              />
            </Box>

            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={isRecurring} name='schedule' value='recur' title='This stage will repeat'
                onChange={e => selectSchedule('recur')}
                content={
                  <Box>
                    <Text color='fg.muted' mb={5}>The stage will dynamically repeat based on your rules.</Text>
                    {isRecurring &&
                      <>
                        <Stack spacing={2} direction='row' justifyContent='center' alignItems='center'>
                          <Text fontSize='sm'>Repeat every</Text>
                          <Input size='xs' bg='bg.surface' w='50px' isDisabled={!isRecurring} placeholder='Days' type='number' value={recurringFrequency ?? ''} onChange={e => setRecurringFrequency(e.target.value)} />
                          <Text fontSize='sm'>days</Text>
                        </Stack>
                        <Stack mt={1} spacing={2} direction='row' justifyContent='center' alignItems='center'>
                          <Text fontSize='sm'>for</Text>
                          <Input size='xs' bg='bg.surface' w='50px' isDisabled={!isRecurring} placeholder='Number' type='number' value={recurringDuration ?? ''} onChange={e => setRecurringDuration(e.target.value)} />
                          <Text fontSize='sm'>occurrences</Text>
                        </Stack>
                        {occurrenceCount > 0 &&
                          <Box mt={3}>
                            <Button size='xs' variant='ghost' width='100%' onClick={() => setShowRecurrenceNames(!recurrenceNamesVisible)}>
                              {recurrenceNamesVisible ? 'Hide occurrence names' : 'Name occurrences individually'}
                            </Button>
                            {recurrenceNamesVisible &&
                              <Box mt={2}>
                                <Text fontSize='xs' color='fg.muted' mb={2}>Give an occurrence its own name, or leave it blank to use the stage name.</Text>
                                <Box maxH='220px' overflowY='auto'>
                                  <Stack gap={1}>
                                    {Array.from({ length: occurrenceCount }, (_, i) => (
                                      <Stack key={i} direction='row' alignItems='center' gap={2}>
                                        <Text fontSize='xs' color='fg.muted' w='18px' textAlign='right'>{i + 1}</Text>
                                        <Input size='xs' bg='bg.surface' flex='1' placeholder={defaultRecurrenceName(i)} value={(recurrenceNames || [])[i] || ''} onChange={e => updateRecurrenceName(i, e.target.value)} />
                                      </Stack>
                                    ))}
                                  </Stack>
                                </Box>
                              </Box>}
                          </Box>}
                      </>}
                  </Box>
                }
              />
            </Box>

            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={adHoc} name='schedule' value='adHoc' title='Ad-hoc input'
                onChange={e => selectSchedule('adHoc')}
                content={
                  <>
                    <Text color='fg.muted' mb={5}>The stage can be completed any number of times and at any point.</Text>
                  </>
                }
              />
            </Box>

            <Box rounded='md' shadow='sm' bg='bg.surface' p={3}>
              <RadioHeader
                checked={isAnchored} name='schedule' value='anchored' title='Anchor-scheduled'
                onChange={e => selectSchedule('anchored')}
                content={
                  <Box onClick={e => e.stopPropagation()}>
                    <Text color='fg.muted' mb={3}>The stage appears at specific days relative to an anchor date.</Text>
                    {isAnchored && (
                      <Stack gap={3}>
                        {anchors?.length === 0
                          ? <Alert status='warning' fontSize='sm'>No anchors defined. Add anchors on the Anchors tab first.</Alert>
                          : (
                            <>
                              {(anchorSchedule || []).map((slot, i) => (
                                <Stack key={i} direction='row' alignItems='center' gap={2} flexWrap='wrap'>
                                  <Select size='xs' bg='bg.surface' w='auto' flex='1' value={slot.anchor} onChange={e => updateAnchorSlot(i, 'anchor', e.target.value)}>
                                    {anchors.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                                  </Select>
                                  <Text fontSize='xs' whiteSpace='nowrap'>offset</Text>
                                  <Input size='xs' bg='bg.surface' w='60px' type='number' value={slot.dayOffset ?? 0} onChange={e => updateAnchorSlot(i, 'dayOffset', e.target.value)} />
                                  <Text fontSize='xs' whiteSpace='nowrap'>days</Text>
                                  <Button size='xs' variant='ghost' colorPalette='red' onClick={() => removeAnchorSlot(i)}>×</Button>
                                </Stack>
                              ))}
                              <Button size='xs' variant='outline' onClick={addAnchorSlot}>+ Add slot</Button>
                            </>
                            )}
                      </Stack>
                    )}
                  </Box>
                }
              />
            </Box>
          </SimpleGrid>
        </RadioGroup>
      </Box>

      <Stack gap={3} p={3} mt={15} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Box>
          <Heading as='h3' size='md'>Availability time</Heading>
          <Text color='fg.subtle' mb={5}>By default stages become available from midnight on their scheduled day. You can specify a different time here. Times are in the participant's local timezone.</Text>
        </Box>
        <Stack direction='row' alignItems='center' justifyContent='start'>
          <Box maxW='300px'><TimeChooser placeholder='Midnight (default)' value={enableAtTime ?? ''} onChange={val => setEnableAtTime(val || null)} /></Box>
          <Button variant='outline' onClick={() => setEnableAtTime(null)}>Unset</Button>
        </Stack>
      </Stack>

      <Stack gap={3} p={3} mt={15} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Box>
          <Heading as='h3' size='md'>Availability window</Heading>
          <Text color='fg.subtle'>Limit how long this stage stays available once it becomes available to a participant.</Text>
        </Box>
        <FormControl
          label='Available for'
          helperText={adHoc
            ? 'Not applicable, as ad-hoc stages are always available on demand.'
            : `Leave blank (the default) to keep this stage available until it's completed. Otherwise, once this many hours have passed since the stage became available, it disappears from the participant's app and no new submissions are accepted (up to a maximum of ${MAX_AVAILABILITY_WINDOW_HOURS} hours, or one year). Data can still be entered on the participant's behalf in the web app.`}
        >
          <Stack direction='row' alignItems='center' justifyContent='start'>
            <Box maxW='120px'>
              <Input
                type='number'
                min={1}
                max={MAX_AVAILABILITY_WINDOW_HOURS}
                placeholder='No limit'
                isDisabled={adHoc}
                value={availabilityWindowHours ?? ''}
                onChange={e => {
                  // Keep the value inside what the API accepts: anything that isn't a
                  // positive whole number (0, a negative, a cleared field) means "no
                  // limit", and anything above the cap snaps to it. Sending an
                  // out-of-range value would fail validation and block the whole save.
                  const hours = parseInt(e.target.value, 10)
                  if (!Number.isFinite(hours) || hours <= 0) return setAvailabilityWindowHours(null)
                  setAvailabilityWindowHours(Math.min(hours, MAX_AVAILABILITY_WINDOW_HOURS))
                }}
              />
            </Box>
            <Text color='fg.subtle'>hours</Text>
            <Button variant='outline' disabled={adHoc} onClick={() => setAvailabilityWindowHours(null)}>Unset</Button>
          </Stack>
        </FormControl>
      </Stack>

      <Stack gap={5} p={3} mt={15} borderWidth='1px' rounded='md' bg='bg.subtle'>
        <Box>
          <Heading as='h3' size='md'>Behind status</Heading>
          <Text color='fg.subtle'>Participants are automatically marked as "behind" when they haven't completed a stage in time. You can control how this stage contributes to that status.</Text>
        </Box>

        <FormControl label='Mark as exempt from "behind" status' helperText='When enabled, this stage will never cause a participant to be marked as behind, no matter how overdue it is.'>
          <Checkbox
            isChecked={exemptFromBehindStatus}
            onChange={e => setExemptFromBehindStatus(e.checked)}
          >
            Ignore this stage when calculating a participant's "behind" status
          </Checkbox>
        </FormControl>

        <FormControl
          label='Behind cutoff time'
          helperText={exemptFromBehindStatus
            ? 'Not applicable, as this stage is exempt from the "behind" status above.'
            : 'By default, this stage counts as behind from midnight on the day after it\'s due. Specify a different cutoff time here to mark it behind as soon as that time passes on the day it becomes due instead. Times are in the participant\'s local timezone.'}
        >
          <Stack direction='row' alignItems='center' justifyContent='start'>
            <Box maxW='300px'>
              <TimeChooser
                placeholder='Midnight the following day (default)'
                value={behindCutoffTime ?? ''}
                disabled={exemptFromBehindStatus}
                onChange={val => setBehindCutoffTime(val || null)}
              />
            </Box>
            <Button variant='outline' disabled={exemptFromBehindStatus} onClick={() => setBehindCutoffTime(null)}>Unset</Button>
          </Stack>
        </FormControl>
      </Stack>
    </Box>
  )
}
